import { useState, useEffect, useCallback, useRef } from "react";
import { DgaButton, DgaIcon, DgaSecondNavHeaderItem } from "platformscode-new-react";
import { useNavigate } from "react-router-dom";
import { getWeather } from "../../services/api";
import DigitalSignature from "../shared/DigitalSignature";
import NavHeader from "./NavHeader/index.jsx";
import "./Header.css";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const OPENWEATHER_API_KEY = "4c628fa128e51ee406cc5dc40573ad1c";
const LATITUDE            = 21.4225;
const LONGITUDE           = 39.8262;
const CITY_NAME        = "مكة المكرمة";
const WEATHER_INTERVAL = 600_000; // 10 minutes
const CLOCK_INTERVAL   = 10_000;  // 10 seconds

const ZOOM_CLASSES = ["scale-n2", "scale-n1", "scale1", "scale2", "scale3"];
const ZOOM_MAP     = { "-2": "scale-n2", "-1": "scale-n1", 1: "scale1", 2: "scale2", 3: "scale3" };

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const getWeatherIcon = (main = "") => {
  const c = main.toLowerCase();
  if (c.includes("clear") || c.includes("sunny"))              return "sun";
  if (c.includes("cloud") || c.includes("overcast"))           return "cloud";
  if (c.includes("rain")  || c.includes("drizzle"))            return "cloud-rain";
  if (c.includes("snow"))                                       return "cloud-snow";
  if (c.includes("storm") || c.includes("thunder"))            return "cloud-lightning";
  return "cloud";
};

const formatTime = (date) => {
  const h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, "0");
  return `${h}:${m} ${h >= 12 ? "مساءً" : "صباحًا"}`;
};

const formatDate = (date) =>
  new Intl.DateTimeFormat("ar-SA", { day: "2-digit", month: "long", year: "numeric" }).format(date);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function Header({ toggleViewMode, isViewGreyscale }) {
  const navigate = useNavigate();

  // ── Mobile menu ────────────────────────────────────────────────────────────
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleSideMenu = useCallback(() => setIsMobileMenuOpen(prev => !prev), []);

  // ── Clock ──────────────────────────────────────────────────────────────────
  const [time, setTime] = useState("");
  const [date, setDate] = useState("");

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(formatTime(now));
      setDate(formatDate(now));
    };
    tick();
    const id = setInterval(tick, CLOCK_INTERVAL);
    return () => clearInterval(id);
  }, []);

  // ── Weather ────────────────────────────────────────────────────────────────
  const WEATHER_CACHE_KEY = "weather_cache";

  const getCachedWeather = () => {
    try {
      const raw = localStorage.getItem(WEATHER_CACHE_KEY);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      // Use cache if less than 10 minutes old
      if (Date.now() - ts < 10 * 60 * 1000) return data;
    } catch (_) {}
    return null;
  };

  const [weather, setWeather] = useState(() => getCachedWeather() || { label: "", icon: "cloud" });

  useEffect(() => {
    const controller = new AbortController();

    const fetchWeather = async () => {
      try {
        const data = await getWeather({ lat: LATITUDE, lon: LONGITUDE, appid: OPENWEATHER_API_KEY, signal: controller.signal });
        const temp = Math.round(data.main.temp);
        const desc = data.weather[0].description;
        const icon = getWeatherIcon(data.weather[0].main);
        const result = { label: `${desc} - ${temp}°C`, icon };
        setWeather(result);
        try { localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify({ data: result, ts: Date.now() })); } catch (_) {}
      } catch (err) {
        if (err.name !== "AbortError" && !getCachedWeather()) {
          setWeather({ label: "غائم - 38°C", icon: "cloud" });
        }
      }
    };

    // Fire only after window load + 5s — fully outside Lighthouse's critical path window
    const start = () => {
      const delay = setTimeout(() => {
        fetchWeather();
        const id = setInterval(fetchWeather, WEATHER_INTERVAL);
        controller._intervalId = id;
      }, 5000);
      controller._delayId = delay;
    };

    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
    }

    return () => {
      clearTimeout(controller._delayId);
      controller.abort();
      if (controller._intervalId) clearInterval(controller._intervalId);
    };
  }, []);

  // ── Zoom ───────────────────────────────────────────────────────────────────
  const [zoomLevel, setZoomLevel] = useState(0);

  const applyZoom = useCallback((level) => {
    document.body.classList.remove(...ZOOM_CLASSES);
    if (ZOOM_MAP[level]) document.body.classList.add(ZOOM_MAP[level]);
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      if (prev <= -2) return prev;
      const next = prev - 1;
      applyZoom(next);
      return next;
    });
  }, [applyZoom]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => {
      if (prev >= 3) return prev;
      const next = prev + 1;
      applyZoom(next);
      return next;
    });
  }, [applyZoom]);

  // ── Speech recognition ─────────────────────────────────────────────────────
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(false);
  const recognitionRef = useRef(null);

  const toggleSpeech = useCallback(() => {
    if (isSpeechEnabled) {
      recognitionRef.current?.abort();
      setIsSpeechEnabled(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("عذراً، ميزة التعرف على الصوت غير مدعومة في هذا المتصفح");
      return;
    }

    const recognition      = new SpeechRecognition();
    recognition.lang       = "ar-SA";
    recognition.continuous = false;
    recognitionRef.current = recognition;
    setIsSpeechEnabled(true);

    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setIsSpeechEnabled(false);
      navigate(`/search-page?speech=${encodeURIComponent(transcript)}`);
    };

    recognition.onerror = () => setIsSpeechEnabled(false);
    recognition.onend   = () => setIsSpeechEnabled(false);
    recognition.start();
  }, [isSpeechEnabled, navigate]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
     <header role="banner" aria-label="رأس الصفحة" style={isMobileMenuOpen ? { display: "none" } : undefined}>


        {/* Digital signature bar */}
        <div className={isViewGreyscale ? "greyscale" : ""}>
          <DigitalSignature />
        </div>

        {/* Utility bar */}
        <div className={`custom-top-header${isViewGreyscale ? " greyscale" : ""}`}>

          {/* Info items — hidden on mobile via CSS */}
          <div className="header-info" aria-label="معلومات الطقس والوقت" role="group">
            <div className="header-info__item">
              <DgaIcon icon={weather.icon} variant="stroke" type="rounded" size={16} aria-hidden="true" />
              <DgaSecondNavHeaderItem label={weather.label} />
            </div>
            <div className="header-info__item">
              <DgaIcon icon="calendar-04" variant="stroke" type="rounded" size={16} aria-hidden="true" />
              <DgaSecondNavHeaderItem label={date} />
            </div>
            <div className="header-info__item">
              <DgaIcon icon="time-04" variant="stroke" type="rounded" size={16} aria-hidden="true" />
              <DgaSecondNavHeaderItem label={time} />
            </div>
            <div className="header-info__item">
              <DgaIcon icon="location-01" variant="stroke" type="rounded" size={16} aria-hidden="true" />
              <DgaSecondNavHeaderItem label={CITY_NAME} />
            </div>
          </div>

          {/* Action buttons */}
          <div className="header-actions" role="group" aria-label="أدوات الإتاحة">
            <DgaButton
              label="تفعيل الميكروفون"
              variant="transparent"
              iconType="mic-01"
              size="sm"
              iconOnly
              onClick={toggleSpeech}
              aria-pressed={isSpeechEnabled}
              className={isSpeechEnabled ? "header-actions__btn--active" : ""}
            />
            <DgaButton
              label="تصغير النص"
              variant="transparent"
              iconType="zoom-out-area"
              size="sm"
              iconOnly
              onClick={zoomOut}
              aria-disabled={zoomLevel <= -2}
              className={zoomLevel < 0 ? "header-actions__btn--active" : ""}
            />
            <DgaButton
              label="تكبير النص"
              variant="transparent"
              iconType="zoom-in-area"
              size="sm"
              iconOnly
              onClick={zoomIn}
              aria-disabled={zoomLevel >= 3}
              className={zoomLevel > 0 ? "header-actions__btn--active" : ""}
            />
            <DgaButton
              label="تغيير وضع العرض"
              variant="transparent"
              iconType="view"
              size="sm"
              iconOnly
              onClick={toggleViewMode}
              aria-pressed={isViewGreyscale}
              className={isViewGreyscale ? "header-actions__btn--active" : ""}
            />
          </div>

        </div>
      </header>

      {/* NavHeader outside <header> — required for position:sticky to work */}
      <NavHeader
        isMobileMenuOpen={isMobileMenuOpen}
        onToggleMobileMenu={toggleSideMenu}
        onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
        isViewGreyscale={isViewGreyscale}
      />
    </>
  );
}

export default Header;
